import axios from "axios";
import { put, select, takeEvery, takeLatest, SelectEffect } from "redux-saga/effects";
import {
    GET_FEEDBACK_LIST,
    GET_FEEDBACK_OPTIONS,
    GET_FEEDBACK_TABLE_FILTERS,
    UPDATE_FEEDBACK,
    GET_SEGMENT_FEEDBACK_COMMENTS,
    POST_SEGMENT_FEEDBACK_COMMENT,
    FeedbackItem,
    FeedbackOptions,
    FeedbackOptionsReceivedAction,
    FeedbackResponseInterface,
    FeedbackTableFilter,
    FeedbackTableFiltersReceivedAction,
    FeedbackListReceivedAction,
    GetFeedbackListAction,
    UpdateFeedbackAction,
    GetSegmentFeedbackCommentsAction,
    PostSegmentFeedbackCommentAction,
    SegmentFeedbackComment,
    SegmentFeedbackCommentsReceivedAction
} from "actions/Feedback/types";
import {
    feedbackListReceived,
    feedbackOptionsReceived,
    feedbackTableFiltersReceived,
    getFeedbackList,
    getSegmentFeedbackComments,
    segmentFeedbackCommentPosted,
    segmentFeedbackCommentsReceived
} from "actions";
import { RootState } from "reducers";
import { GeneratorYieldType } from "interfaces/ActionReducer/GetMethod/GetResponse";
import { showError } from "components/commonUtilities";
import { showToast } from "components/DesignSystem";

const feedbackOptionsSelector = (state: RootState): FeedbackOptions =>
    state.Feedback.feedbackOptions;

// Options are static; the in-flight request is shared so concurrent callers only fetch once.
let feedbackOptionsRequest: Promise<FeedbackOptions> | null = null;

type GetFeedbackOptionsGenerator = Generator<
    | SelectEffect
    | Promise<FeedbackOptions>
    | GeneratorYieldType<FeedbackOptionsReceivedAction["payload"]>,
    FeedbackOptions,
    FeedbackOptions
>;

function* getFeedbackOptionsSaga(): GetFeedbackOptionsGenerator {
    let options = yield select(feedbackOptionsSelector);

    if (options.responder_type.length && options.response_category.length) return options;

    try {
        if (!feedbackOptionsRequest) {
            feedbackOptionsRequest = axios
                .get("/api/segment-feedback/get_feedback_options/")
                .then((res) => res.data);
        }

        options = yield feedbackOptionsRequest;

        yield put(feedbackOptionsReceived({ feedbackOptions: options }));
    } catch (err) {
        feedbackOptionsRequest = null;
        showError(err);
    }

    return options;
}

export function* getFeedbackOptionsWatcherSaga() {
    yield takeLatest(GET_FEEDBACK_OPTIONS, getFeedbackOptionsSaga);
}

function* getFeedbackTableFiltersSaga(): Generator<
    | Promise<FeedbackTableFilter[]>
    | GeneratorYieldType<FeedbackTableFiltersReceivedAction["payload"]>,
    void,
    FeedbackTableFilter[]
> {
    try {
        const feedbackTableFilters = yield axios
            .get("/api/segment-feedback/segment_feedback_table_config/")
            .then((res) => res.data);

        yield put(feedbackTableFiltersReceived({ feedbackTableFilters }));
    } catch (err) {
        showError(err);
    }
}

export function* getFeedbackTableFiltersWatcherSaga() {
    yield takeLatest(GET_FEEDBACK_TABLE_FILTERS, getFeedbackTableFiltersSaga);
}

function* getFeedbackListSaga(
    action: GetFeedbackListAction
): Generator<
    | GetFeedbackOptionsGenerator
    | Promise<FeedbackResponseInterface | FeedbackItem[]>
    | GeneratorYieldType<FeedbackListReceivedAction["payload"]>,
    void,
    FeedbackResponseInterface | FeedbackItem[]
> {
    try {
        yield getFeedbackOptionsSaga();

        const { params } = action.payload;
        let url = "api/segment-feedback/?";

        Object.keys(params).forEach((prop) => {
            const value = params[prop];

            // Send array filters as comma-separated values, not JSON-stringified arrays
            url += `&${prop}=${Array.isArray(value) ? value.toString() : value}`;
        });

        const response = yield axios.get(url).then((res) => res.data);
        const results: FeedbackItem[] = Array.isArray(response)
            ? response
            : response?.results ?? [];
        const meta = Array.isArray(response) ? undefined : response?.meta;

        yield put(
            feedbackListReceived({
                feedbackList: {
                    meta: meta ?? {
                        count: results.length,
                        next: null,
                        previous: null
                    },
                    results
                }
            })
        );
    } catch (err) {
        yield put(
            feedbackListReceived({
                feedbackList: {
                    meta: {
                        count: 0,
                        next: null,
                        previous: null
                    },
                    results: []
                }
            })
        );
        showError(err);
    }
}
export function* getFeedbackListWatcherSaga() {
    yield takeLatest(GET_FEEDBACK_LIST, getFeedbackListSaga);
}

function* updateFeedbackSaga(
    action: UpdateFeedbackAction
): Generator<
    Promise<void> | SelectEffect | GeneratorYieldType<GetFeedbackListAction["payload"]>,
    void,
    RootState["Feedback"]["feedbackParams"]
> {
    try {
        yield axios
            .patch(`/api/segment-feedback/${action.payload.id}/`, action.payload.params)
            .then(() => undefined);

        const params = yield select((state: RootState) => state.Feedback.feedbackParams);

        yield put(getFeedbackList({ params }));
        action.payload.afterProcess?.();
        showToast("Feedback has been updated successfully", { type: "success" });
    } catch (err) {
        showError(err);
    }
}

export function* updateFeedbackWatcherSaga() {
    yield takeEvery(UPDATE_FEEDBACK, updateFeedbackSaga);
}

function* getSegmentFeedbackCommentsSaga(
    action: GetSegmentFeedbackCommentsAction
): Generator<
    | Promise<SegmentFeedbackComment | SegmentFeedbackComment[]>
    | GeneratorYieldType<SegmentFeedbackCommentsReceivedAction["payload"]>,
    void,
    SegmentFeedbackComment | SegmentFeedbackComment[]
> {
    try {
        const response = yield axios
            .get("/api/segment-feedback-comments/get_comments/", {
                params: { segment_feedback_id: action.payload.segment_feedback_id }
            })
            .then((res) => res.data);

        // API may return a single root comment or a list of root comments
        const segmentFeedbackComments = Array.isArray(response)
            ? response
            : [response].filter(Boolean);

        yield put(segmentFeedbackCommentsReceived({ segmentFeedbackComments }));
    } catch (err) {
        // Reset the fetch flag so the modal is not stuck with disabled controls
        yield put(segmentFeedbackCommentsReceived({ segmentFeedbackComments: [] }));
        showError(err);
    }
}

export function* getSegmentFeedbackCommentsWatcherSaga() {
    yield takeLatest(GET_SEGMENT_FEEDBACK_COMMENTS, getSegmentFeedbackCommentsSaga);
}

function* postSegmentFeedbackCommentSaga(
    action: PostSegmentFeedbackCommentAction
): Generator<
    | Promise<void>
    | GeneratorYieldType<GetSegmentFeedbackCommentsAction["payload"]>
    | GeneratorYieldType<void>,
    void,
    void
> {
    try {
        yield axios
            .post("/api/segment-feedback-comments/", action.payload.params)
            .then(() => undefined);

        yield put(
            getSegmentFeedbackComments({
                segment_feedback_id: action.payload.params.segment_feedback_id
            })
        );
        action.payload.afterProcess?.();
        showToast("Comment has been submitted successfully", { type: "success" });
    } catch (err) {
        showError(err);
    } finally {
        // Release in-flight POST state on both success and failure paths
        yield put(segmentFeedbackCommentPosted());
    }
}

export function* postSegmentFeedbackCommentWatcherSaga() {
    yield takeLatest(POST_SEGMENT_FEEDBACK_COMMENT, postSegmentFeedbackCommentSaga);
}
